import React, { useState, useEffect, useContext, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import styled from "styled-components";
import { useHistory, useParams } from "react-router-dom";
import axiosInstance from "../../../../../appHelpers/axiosInstance";
import {
  Input,
  InputGroup,
  Message,
  Popover,
  useToaster,
  Whisper,
} from "rsuite";
import { DateTime } from "luxon";
import PlusIcon from "@rsuite/icons/Plus";
import CloseIcon from "@rsuite/icons/Close";
import { UpdateIssue } from "./Modal/UpdateIssue";
import { InputContext } from "../../_context/InputContext";
import DetailIcon from "@rsuite/icons/Detail";
import SearchIcon from "@rsuite/icons/Search";
import {
  Button,
  GlassCard,
  Badge,
  SegmentedPicker,
  Picker,
  colors,
} from "../../../../ui";

export const IssueTrackerFC = () => {
  const triggerRef = useRef();
  const history = useHistory();
  const toaster = useToaster();
  const [addSystem, setAddSystem] = useState("");
  const { getAccessTokenSilently } = useAuth0();
  const [issues, setIssues] = useState();
  const [displayIssues, setDisplayIssues] = useState();
  const [update, setUpdate] = useState(false);
  const [roles, setRoles] = useState();
  const [loadingNetworkIssues, setLoadingNetworkIssues] = useState([]);
  const [processorPicker, setProcessorPicker] = useState("All");
  const [resolvedPicker, setResolvedPicker] = useState("All");
  const [reportablePicker, setReportablePicker] = useState("All");
  const [assignedPicker, setAssignedPicker] = useState(null);
  const [typePicker, setTypePicker] = useState(null);
  const [sortBy, setSortBy] = useState("days-active-desc");
  const [searchQuery, setSearchQuery] = useState("");

  const { adminCustomerToggle, setAdminCustomerToggle } =
    useContext(InputContext);

  const assignedSelectData = [
    ...new Set(
      issues
        ?.map((issue) => issue.assigned)
        .filter((item) => item !== null && item !== undefined),
    ),
  ].map((item) => ({
    label: item,
    value: item,
  }));

  const selectDataType = [
    "OFFLINE",
    "MAGNET",
    "EDU",
    "Credentials",
    "Host Configuration",
  ].map((item) => ({
    label: item,
    value: item,
  }));

  const sortOptions = [
    { label: "Days Active (Oldest)", value: "days-active-desc" },
    { label: "Days Active (Newest)", value: "days-active-asc" },
    { label: "Recently Updated", value: "days-updated-asc" },
    { label: "Least Recently Updated", value: "days-updated-desc" },
  ];

  const getNetworkIssues = async () => {
    setLoadingNetworkIssues(true);

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const res = await axiosInstance({
        url: `/network/issues`,
        data: ``,
        headers: { authorization: `Bearer ${token}` },
      });

      setIssues(res?.data[0]);
    } catch (error) {
      console.error(error);
    }

    setLoadingNetworkIssues(false);
  };

  const handleAddSystem = async () => {
    if (!addSystem || addSystem.trim() === "") {
      toaster.push(
        <Message showIcon type="warning" header="Enter System ID">
          Please enter a system ID to create an issue.
        </Message>,
        "topCenter",
      );
      return;
    }

    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      // Create a basic issue with minimal data
      await axiosInstance({
        method: "POST",
        url: `/network/add/issue`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          sme: addSystem,
          issueData: {
            id: addSystem,
            reportable: false,
            report_users: [],
            type: "OFFLINE",
            notes: "",
            assigned: null,
            processor_type: "MMB",
          },
        }),
      });

      toaster.push(
        <Message showIcon type="success" header="Success">
          Issue created for system {addSystem}. Please update with details.
        </Message>,
        "topCenter",
      );

      // Clear the input and refresh the list
      setAddSystem("");
      setUpdate(!update);
    } catch (error) {
      console.error("Error creating issue:", error);
      toaster.push(
        <Message showIcon type="error" header="Error">
          Failed to create issue. Please verify the system ID and try again.
        </Message>,
        "topCenter",
      );
    }
  };

  const handleRemoveSystem = async (e) => {
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "PUT",
        url: `/network/delete/issue`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          issue: { report_id: e },
        }),
      });
      toaster.push(message, "topCenter");
      setUpdate(!update);
    } catch (error) {
      console.error(error);
      toaster.push(errMessage, "topCenter");
    }
  };

  const filterIssues = () => {
    let tempDisplayIssues = issues?.filter((issue) => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const searchText =
          `${issue.id} ${issue.customer_name} ${issue.site_name} ${issue.type} ${issue.assigned || ""} ${issue.notes || ""} ${issue.diagnoses || ""}`.toLowerCase();
        if (!searchText.includes(query)) {
          return false;
        }
      }

      // Other filters
      return (
        (resolvedPicker === "All" ||
          (issue.resolved ? "True" : "False") === resolvedPicker) &&
        (processorPicker === "All" ||
          issue.processor_type === processorPicker) &&
        (reportablePicker === "All" ||
          (reportablePicker === "Reportable" && issue.reportable) ||
          (reportablePicker === "Not Reportable" && !issue.reportable)) &&
        (assignedPicker === null || issue.assigned === assignedPicker) &&
        (typePicker === null || issue.type === typePicker) &&
        (adminCustomerToggle === null ||
          issue.customer_id === adminCustomerToggle) &&
        issue.status === "active"
      );
    });

    // Apply sorting
    if (tempDisplayIssues) {
      tempDisplayIssues = [...tempDisplayIssues].sort((a, b) => {
        const now = DateTime.local();

        switch (sortBy) {
          case "days-active-desc":
            // Oldest issues first (most days active)
            const daysActiveA = Math.floor(
              now.diff(DateTime.fromISO(a.inserted_at), "days").days,
            );
            const daysActiveB = Math.floor(
              now.diff(DateTime.fromISO(b.inserted_at), "days").days,
            );
            return daysActiveB - daysActiveA;

          case "days-active-asc":
            // Newest issues first (least days active)
            const daysActiveA2 = Math.floor(
              now.diff(DateTime.fromISO(a.inserted_at), "days").days,
            );
            const daysActiveB2 = Math.floor(
              now.diff(DateTime.fromISO(b.inserted_at), "days").days,
            );
            return daysActiveA2 - daysActiveB2;

          case "days-updated-asc":
            // Recently updated first (least days since update)
            const daysUpdatedA = Math.floor(
              now.diff(DateTime.fromISO(a.updated_at), "days").days,
            );
            const daysUpdatedB = Math.floor(
              now.diff(DateTime.fromISO(b.updated_at), "days").days,
            );
            return daysUpdatedA - daysUpdatedB;

          case "days-updated-desc":
            // Least recently updated first (most days since update)
            const daysUpdatedA2 = Math.floor(
              now.diff(DateTime.fromISO(a.updated_at), "days").days,
            );
            const daysUpdatedB2 = Math.floor(
              now.diff(DateTime.fromISO(b.updated_at), "days").days,
            );
            return daysUpdatedB2 - daysUpdatedA2;

          default:
            return 0;
        }
      });
    }

    setDisplayIssues(tempDisplayIssues);
  };

  useEffect(() => {
    getNetworkIssues();
  }, [update]);

  useEffect(() => {
    filterIssues();
  }, [issues, adminCustomerToggle]);

  useEffect(() => {
    filterIssues();
  }, [
    resolvedPicker,
    processorPicker,
    reportablePicker,
    typePicker,
    assignedPicker,
    sortBy,
    searchQuery,
  ]);

  // Helper functions for status calculations
  const getOpenIssueCount = () => {
    const filtered = displayIssues?.filter(
      (issue) =>
        !issue.resolved &&
        (adminCustomerToggle === null ||
          issue.customer_id === adminCustomerToggle),
    ).length;
    const total = issues?.filter(
      (issue) =>
        !issue.resolved &&
        issue.status === "active" &&
        (adminCustomerToggle === null ||
          issue.customer_id === adminCustomerToggle),
    ).length;
    return filtered === total ? total : `${filtered}/${total}`;
  };

  const getClosedIssueCount = () => {
    const filtered = displayIssues?.filter(
      (issue) =>
        issue.resolved &&
        (adminCustomerToggle === null ||
          issue.customer_id === adminCustomerToggle),
    ).length;
    const total = issues?.filter(
      (issue) =>
        issue.resolved &&
        issue.status === "active" &&
        (adminCustomerToggle === null ||
          issue.customer_id === adminCustomerToggle),
    ).length;
    return filtered === total ? total : `${filtered}/${total}`;
  };

  const getCustomerCount = () => {
    const filteredCustomers = [
      ...new Set(displayIssues?.map((issue) => issue.customer_name)),
    ].length;
    const totalCustomers = [
      ...new Set(
        issues
          ?.filter((issue) => issue.status === "active")
          .map((issue) => issue.customer_name),
      ),
    ].length;
    return filteredCustomers === totalCustomers
      ? totalCustomers
      : `${filteredCustomers}/${totalCustomers}`;
  };

  return (
    <NetworkIssueListSC>
      {/* Combined Status and Filters Card */}
      <GlassCard>
        <CombinedContainer>
          {/* Status Metrics Row */}
          <StatusMetricsRow>
            <StatusMetric>
              <MetricIcon variant="error"></MetricIcon>
              <MetricContent>
                <MetricLabel>Open Issues</MetricLabel>
                <MetricValue variant="error">{getOpenIssueCount()}</MetricValue>
              </MetricContent>
            </StatusMetric>

            <MetricDivider />

            <StatusMetric>
              <MetricIcon variant="success"></MetricIcon>
              <MetricContent>
                <MetricLabel>Closed Issues</MetricLabel>
                <MetricValue variant="success">
                  {getClosedIssueCount()}
                </MetricValue>
              </MetricContent>
            </StatusMetric>

            <MetricDivider />

            <StatusMetric>
              <MetricIcon variant="info"></MetricIcon>
              <MetricContent>
                <MetricLabel>Customers</MetricLabel>
                <MetricValue variant="info">{getCustomerCount()}</MetricValue>
              </MetricContent>
            </StatusMetric>
          </StatusMetricsRow>

          <HorizontalDivider />

          {/* First Filter Row: Segmented Pickers */}
          <SegmentedFiltersRow>
            <SegmentedPickerGroup>
              <SegmentedPicker
                options={[
                  { eventKey: "All", label: "All" },
                  { eventKey: "MMB", label: "MMB" },
                  { eventKey: "HHM", label: "HHM" },
                ]}
                activeKey={processorPicker}
                onSelect={setProcessorPicker}
                size="small"
                variant="subtle"
              />
            </SegmentedPickerGroup>

            <SegmentedPickerGroup>
              <SegmentedPicker
                options={[
                  { eventKey: "All", label: "All" },
                  { eventKey: "False", label: "Open" },
                  { eventKey: "True", label: "Closed" },
                ]}
                activeKey={resolvedPicker}
                onSelect={setResolvedPicker}
                size="small"
                variant="subtle"
              />
            </SegmentedPickerGroup>

            <SegmentedPickerGroup>
              <SegmentedPicker
                options={[
                  { eventKey: "All", label: "All" },
                  { eventKey: "Reportable", label: "Reportable" },
                  { eventKey: "Not Reportable", label: "Not Reportable" },
                ]}
                activeKey={reportablePicker}
                onSelect={setReportablePicker}
                size="small"
                variant="subtle"
              />
            </SegmentedPickerGroup>
          </SegmentedFiltersRow>

          {/* Search Bar */}
          <SearchBarContainer>
            <StyledInputGroup inside>
              <StyledInput
                placeholder="Search by System ID, Customer, Site, Type, Assignee, Notes..."
                value={searchQuery}
                onChange={setSearchQuery}
              />
              <InputGroup.Addon>
                <SearchIcon style={{ color: colors.neutral[500] }} />
              </InputGroup.Addon>
            </StyledInputGroup>
          </SearchBarContainer>

          {/* Second Filter Row: Regular Pickers */}
          <RegularPickersRow>
            <FilterGroup>
              <Picker
                data={selectDataType}
                value={typePicker}
                onChange={setTypePicker}
                placeholder="All Types"
                searchable={false}
                width="140px"
              />
            </FilterGroup>

            <FilterGroup>
              <Picker
                data={assignedSelectData}
                value={assignedPicker}
                onChange={setAssignedPicker}
                placeholder="All Assignees"
                searchable={false}
                width="160px"
              />
            </FilterGroup>

            <FilterGroup>
              <Picker
                data={sortOptions}
                value={sortBy}
                onChange={setSortBy}
                placeholder="Sort by..."
                searchable={false}
                width="200px"
              />
            </FilterGroup>
          </RegularPickersRow>
        </CombinedContainer>
      </GlassCard>

      <AddSystemCard>
        <AddSystemHeader>
          <AddSystemTitle>Add System Issue</AddSystemTitle>
          <AddSystemGroup>
            <SystemInput
              placeholder="System ID"
              value={addSystem}
              onChange={setAddSystem}
            />
            <Button variant="primary" size="small" onClick={handleAddSystem}>
              Create Issue
            </Button>
          </AddSystemGroup>
        </AddSystemHeader>
      </AddSystemCard>

      {/* Table Header
      <TableHeader>
        <HeaderItem flex="0.5"></HeaderItem>
        <HeaderItem flex="2">Reported</HeaderItem>
        <HeaderItem flex="1">Days</HeaderItem>
        <HeaderItem flex="3">Customer</HeaderItem>
        <HeaderItem flex="1">ID</HeaderItem>
        <HeaderItem flex="1.5">Type</HeaderItem>
        <HeaderItem flex="2">Assigned</HeaderItem>
        <HeaderItem flex="1">Reportable</HeaderItem>
        <HeaderItem flex="1">Actions</HeaderItem>
      </TableHeader>*/}

      {/* Issues List */}
      <IssuesList>
        {displayIssues?.map((issue) => (
          <IssueCard key={issue.report_id} resolved={issue.resolved}>
            <IssueRow>
              <IssueCell flex="0.5">
                <Whisper
                  placement="right"
                  speaker={
                    <Popover title="Issue Details" style={{ width: "400px" }}>
                      <PopoverContent>
                        <DetailSection>
                          <DetailLabel>Notes:</DetailLabel>
                          <DetailText>{issue.notes}</DetailText>
                        </DetailSection>
                        <DetailSection>
                          <DetailLabel>Diagnosis:</DetailLabel>
                          <DetailText>
                            {issue.diagnoses || "No diagnosis yet"}
                          </DetailText>
                        </DetailSection>
                        <DetailSection>
                          <DetailLabel>Updated By:</DetailLabel>
                          <DetailText>{issue.updated_by}</DetailText>
                        </DetailSection>
                        <DetailSection>
                          <DetailLabel>Last Updated:</DetailLabel>
                          <DetailText>
                            {DateTime.fromISO(issue.updated_at).toFormat("ff")}
                          </DetailText>
                        </DetailSection>
                      </PopoverContent>
                    </Popover>
                  }
                  trigger="hover"
                >
                  <DetailIcon
                    style={{
                      fontSize: "1.5em",
                      color: "#4a90e2",
                      cursor: "pointer",
                    }}
                  />
                </Whisper>
              </IssueCell>

              <IssueCell flex="2">
                <DateTimeText>
                  {DateTime.fromISO(issue.inserted_at).toFormat("MMM dd, yyyy")}
                </DateTimeText>
                <TimeStamp>
                  {DateTime.fromISO(issue.inserted_at).toFormat("h:mm a")}
                </TimeStamp>
              </IssueCell>

              <IssueCell flex="1">
                <DaysBadge variant={issue.resolved ? "success" : "error"}>
                  {issue.resolved
                    ? Math.floor(
                        DateTime.local().diff(
                          DateTime.fromISO(issue.updated_at),
                          "days",
                        ).days,
                      )
                    : Math.floor(
                        DateTime.local().diff(
                          DateTime.fromISO(issue.inserted_at),
                          "days",
                        ).days,
                      )}
                  d
                </DaysBadge>
              </IssueCell>

              <IssueCell flex="3">
                <CustomerName>{issue.customer_name}</CustomerName>
                <SiteName>{issue.site_name}</SiteName>
              </IssueCell>

              <IssueCell flex="1">
                {issues.filter(
                  (archived) =>
                    archived.id === issue.id &&
                    (archived.status === "removed" || archived.resolved) &&
                    archived.report_id !== issue.report_id,
                ).length > 0 ? (
                  <Whisper
                    placement="right"
                    speaker={
                      <Popover
                        title="Previous Issues"
                        style={{ width: "500px" }}
                      >
                        <PopoverContent>
                          {issues
                            .filter(
                              (archived) =>
                                archived.id === issue.id &&
                                (archived.status === "removed" ||
                                  archived.resolved) &&
                                archived.report_id !== issue.report_id,
                            )
                            .map((archIssue) => (
                              <PreviousIssue key={archIssue.report_id}>
                                <IssueDate>
                                  {DateTime.fromISO(
                                    archIssue.inserted_at,
                                  ).toFormat("ff")}
                                </IssueDate>
                                <IssueNotes>{archIssue.notes}</IssueNotes>
                                {archIssue.diagnoses && (
                                  <IssueDiagnosis>
                                    <strong>Diagnosis:</strong>{" "}
                                    {archIssue.diagnoses}
                                  </IssueDiagnosis>
                                )}
                                <IssueUpdater>
                                  By: {archIssue.updated_by}
                                </IssueUpdater>
                              </PreviousIssue>
                            ))}
                        </PopoverContent>
                      </Popover>
                    }
                    trigger="hover"
                  >
                    <SystemId hasHistory>{issue.id}</SystemId>
                  </Whisper>
                ) : (
                  <SystemId>{issue.id}</SystemId>
                )}
              </IssueCell>

              <IssueCell flex="1.5">
                <TypeBadge variant={getTypeVariant(issue.type)}>
                  {issue.type}
                </TypeBadge>
              </IssueCell>

              <IssueCell flex="2">
                <AssignedTo>{issue.assigned || "Unassigned"}</AssignedTo>
              </IssueCell>

              <IssueCell flex="1">
                {issue.reportable ? (
                  <Whisper
                    placement="left"
                    speaker={
                      <Popover title="Report Users" style={{ width: "300px" }}>
                        <PopoverContent>
                          <UsersList>
                            {issue.report_users?.map((user, index) => (
                              <UserItem key={index}>{user}</UserItem>
                            ))}
                          </UsersList>
                        </PopoverContent>
                      </Popover>
                    }
                    trigger="hover"
                  >
                    <ReportableStatus variant="success">
                      REPORTABLE
                    </ReportableStatus>
                  </Whisper>
                ) : (
                  <ReportableStatus variant="error">
                    NOT REPORTABLE
                  </ReportableStatus>
                )}
              </IssueCell>

              <IssueCell flex="1">
                <ActionButtons>
                  <UpdateIssue
                    issue={issue}
                    update={update}
                    setUpdate={setUpdate}
                  />
                  <Button
                    variant="subtle"
                    size="small"
                    onClick={() => handleRemoveSystem(issue.report_id)}
                  >
                    <CloseIcon />
                  </Button>
                </ActionButtons>
              </IssueCell>
            </IssueRow>
          </IssueCard>
        ))}
      </IssuesList>
    </NetworkIssueListSC>
  );
};

// Helper function for type badge variants
const getTypeVariant = (type) => {
  switch (type) {
    case "OFFLINE":
      return "error";
    case "MAGNET":
      return "warning";
    case "EDU":
      return "info";
    case "Credentials":
      return "warning";
    case "Host Configuration":
      return "info";
    default:
      return "info";
  }
};

// Styled Components
const NetworkIssueListSC = styled.div`
  padding: 1rem;
  max-width: 1400px;
  margin: 0 auto;
`;

const AddSystemCard = styled(GlassCard)`
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin-top: 1rem;
  margin-bottom: 1rem;
  width: 50%;
  left: 50%;
  transform: translateX(-50%);
`;

const AddSystemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.25rem 0.5rem;
  position: relative; /* allow absolute centering of inner group */
`;

const AddSystemTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  margin: 0;
  color: rgba(0, 0, 0, 0.8);
`;

const AddSystemGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

/* Pill-shaped input for the Add System card */
const SystemInput = styled(Input)`
  border-radius: 999px !important;
  padding: 6px 12px !important;
  height: 36px;
  box-sizing: border-box;
`;

const CombinedContainer = styled.div`
  padding: 1rem 1.5rem;
`;

const StatusMetricsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-around;
  gap: 2rem;
  padding: 0.5rem 0;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const StatusMetric = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  justify-content: center;
`;

const MetricIcon = styled.div`
  font-size: 1.5rem;
  opacity: 0.8;
`;

const MetricContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
`;

const MetricLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: #2c3e50;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MetricValue = styled.div`
  font-size: 1.4rem;
  font-weight: 700;
  line-height: 1;
  color: ${({ variant }) => {
    switch (variant) {
      case "error":
        return "#e74c3c";
      case "success":
        return "#27ae60";
      case "info":
        return "#4a90e2";
      default:
        return "#2c3e50";
    }
  }};
`;

const MetricDivider = styled.div`
  width: 1px;
  height: 40px;
  background: linear-gradient(
    180deg,
    transparent,
    rgba(0, 0, 0, 0.1),
    transparent
  );

  @media (max-width: 768px) {
    display: none;
  }
`;

const HorizontalDivider = styled.div`
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    rgba(0, 0, 0, 0.1),
    transparent
  );
  margin: 1rem 0;
`;

const SegmentedFiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const SearchBarContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 600px;
  margin: 0 auto 1rem auto;
`;

const StyledInputGroup = styled(InputGroup)`
  width: 100%;
  border-radius: 25px;

  .rs-input-group-addon {
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.3);
    border-left: none;
    border-radius: 0 50px 50px 0;
  }
`;

const StyledInput = styled(Input)`
  border-radius: 50px 0 0 50px !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  background: rgba(255, 255, 255, 0.1) !important;
  backdrop-filter: blur(8px);
  padding: 8px 20px !important;
  height: 38px !important;
  color: ${colors.neutral[800]} !important;

  &::placeholder {
    color: ${colors.neutral[500]} !important;
  }

  &:hover {
    border-color: rgba(255, 255, 255, 0.4) !important;
    background: rgba(255, 255, 255, 0.15) !important;
  }

  &:focus {
    border-color: ${colors.primary.main} !important;
    background: rgba(255, 255, 255, 0.2) !important;
    box-shadow: 0 0 0 2px rgba(44, 90, 160, 0.1) !important;
  }
`;

const RegularPickersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: center;
  justify-content: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`;

const SegmentedPickerGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
`;

const IssuesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const IssueCard = styled(GlassCard)`
  border-left: 4px solid ${({ resolved }) => (resolved ? "#27ae60" : "#e74c3c")};
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  }
`;

const IssueRow = styled.div`
  display: flex;
  align-items: center;
  padding: 1rem 2rem;
  min-height: 60px;
`;

const IssueCell = styled.div`
  flex: ${({ flex }) => flex || 1};
  display: flex;
  flex-direction: column;
  justify-content: center;
  max-height: 20px;
`;

const DateTimeText = styled.div`
  font-size: 0.9rem;
  color: #2c3e50;
  font-weight: 500;
`;

const TimeStamp = styled.div`
  font-size: 0.8rem;
  color: #7f8c8d;
`;

const DaysBadge = styled(Badge)`
  font-weight: 600;
  font-size: 0.8rem;
  margin-right: 1rem;
`;

const TypeBadge = styled(Badge)`
  margin-right: 1rem;
`;

const CustomerName = styled.div`
  font-size: 0.9rem;
  color: #2c3e50;
  font-weight: 500;
  line-height: 1.2;
`;

const SiteName = styled.div`
  font-size: 0.75rem;
  color: #7f8c8d;
  line-height: 1.2;
`;

const SystemId = styled.div`
  font-size: 0.9rem;
  color: #2c3e50;
  font-weight: 500;
  text-decoration: ${({ hasHistory }) => (hasHistory ? "underline" : "none")};
  cursor: ${({ hasHistory }) => (hasHistory ? "pointer" : "default")};
`;

const AssignedTo = styled.div`
  font-size: 0.85rem;
  color: #5d6d7e;
  font-style: ${({ children }) =>
    children === "Unassigned" ? "italic" : "normal"};
  opacity: ${({ children }) => (children === "Unassigned" ? 0.6 : 1)};
`;

const ReportableStatus = styled.div`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${({ variant }) => (variant === "success" ? "#27ae60" : "#e74c3c")};
  cursor: ${({ variant }) => (variant === "success" ? "pointer" : "default")};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding-left: 1rem;
`;

// Popover content styles
const PopoverContent = styled.div`
  padding: 0.5rem;
`;

const DetailSection = styled.div`
  margin-bottom: 1rem;
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailLabel = styled.div`
  font-weight: 600;
  font-size: 0.85rem;
  margin-bottom: 0.25rem;
  color: #333;
`;

const DetailText = styled.div`
  font-size: 0.8rem;
  color: #666;
  line-height: 1.4;
`;

const PreviousIssue = styled.div`
  padding: 0.75rem;
  border-bottom: 1px solid #eee;
  &:last-child {
    border-bottom: none;
  }
`;

const IssueDate = styled.div`
  font-weight: 600;
  font-size: 0.8rem;
  color: #333;
  margin-bottom: 0.25rem;
`;

const IssueNotes = styled.div`
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.25rem;
`;

const IssueDiagnosis = styled.div`
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.25rem;
`;

const IssueUpdater = styled.div`
  font-size: 0.75rem;
  color: #999;
  font-style: italic;
`;

const UsersList = styled.ul`
  margin: 0;
  padding-left: 1rem;
`;

const UserItem = styled.li`
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 0.25rem;
`;

// Toast messages
const message = (
  <Message showIcon type="success" header="Success">
    Operation completed successfully.
  </Message>
);

const errMessage = (
  <Message showIcon type="error" header="Error">
    Something went wrong. Please try again.
  </Message>
);
